import * as React from 'react';

/** @jsxFrag React.Fragment */
import { Colors, Grid } from '@coursera/cds-core';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import MobileAccountMenuGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileAccountMenuGridItem';
import MobileMenuIconGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuIconGridItem';
import AccountMenuConfig from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuConfig';
import AccountSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/AccountSubMenu';
import useShouldShowHelpDropdown from 'bundles/composable-page-header/hooks/useShouldShowHelpDropdown';
import SimpleMobileMenuConfig from 'bundles/composable-page-header/module-renderers/SimpleMobileMenuConfig';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

type Props = {
  customHeaderLogos?: Array<React.ReactNode>;
  acctDropdownLogoutOnly?: boolean;
  enableCourseraLogoLink?: boolean;
  showProgramSwitcher?: boolean;
  showLanguageDropdown?: boolean;
  showHelpDropdown?: boolean;
  showAcctDropdown?: boolean;
  showAcctItemsInMain?: boolean;
  showSignupBtn?: boolean;
  showLoginBtn?: boolean;
  showMobileHamburger?: boolean;
};

const LoggedInConsumerSimpleMobileMainNav = ({
  customHeaderLogos = undefined,
  enableCourseraLogoLink = true,
  showProgramSwitcher = false,
  showLanguageDropdown = true,
  showHelpDropdown = true,
  acctDropdownLogoutOnly = false,
  showAcctDropdown = true,
  showAcctItemsInMain = false,
  showSignupBtn = true,
  showLoginBtn = true,
  showMobileHamburger = true,
}: Props) => {
  const {
    enrolledDegreePrograms,
    chosenDegree,
    isCourseraPlusLearner,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
  } = useConsumerMyPageHeader();

  const isOrgScoped = useIsOrgScopedHome();
  const shouldShowHelpDropdown = useShouldShowHelpDropdown();

  const experienceSwitcherProps = {
    currentOrganization: naptimeChosenOrganization,
    currentDegree: chosenDegree,
    degrees: enrolledDegreePrograms,
    organizations: naptimeOrganizationsWithProgramEnrollments,
  };

  const courseraLogo = (
    <HomeLogoGridItemModule
      href={enableCourseraLogoLink ? '/' : undefined}
      logos={
        customHeaderLogos?.length
          ? customHeaderLogos
          : [<CourseraHomeLogo key="1" logoColor={!enableCourseraLogoLink ? Colors.gray[900] : undefined} />]
      }
    />
  );

  return (
    <MainNavigationWrapper>
      <Grid item container alignItems="center" justifyContent="space-between">
        <Grid item xs={8} sm={6} container alignItems="center">
          {showMobileHamburger && !isOrgScoped && (
            <MobileMenuIconGridItem
              menuConfig={
                <SimpleMobileMenuConfig
                  acctDropdownLogoutOnly={acctDropdownLogoutOnly}
                  showProgramSwitcher={showProgramSwitcher}
                  showLanguageDropdown={showLanguageDropdown}
                  showAcctDropdown={showAcctDropdown}
                  showAcctItemsInMain={showAcctItemsInMain}
                  showSignupBtn={showSignupBtn}
                  showLoginBtn={showLoginBtn}
                />
              }
              homeLogo={courseraLogo}
            />
          )}
          {courseraLogo}
        </Grid>
        {showHelpDropdown && shouldShowHelpDropdown && (
          <Grid item>
            <HelpDropdownGridItem disableChatSsr={true} />
          </Grid>
        )}
        {isOrgScoped && (
          <>
            {showAcctDropdown && (
              <MobileAccountMenuGridItem
                isCourseraPlusLearner={Boolean(isCourseraPlusLearner)}
                menuConfig={
                  <AccountMenuConfig
                    naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                    canViewEducatorAdminLink={canViewEducatorAdminLink}
                    hasCheatingIncidents={hasCheatingIncidents}
                    {...(showProgramSwitcher && experienceSwitcherProps)}
                  />
                }
              />
            )}

            {acctDropdownLogoutOnly && (
              <MobileAccountMenuGridItem
                isCourseraPlusLearner={Boolean(isCourseraPlusLearner)}
                menuConfig={<AccountSubMenu dropdownLinks={[]} />}
                hideFooter
              />
            )}
          </>
        )}
      </Grid>
    </MainNavigationWrapper>
  );
};

export default LoggedInConsumerSimpleMobileMainNav;
